
//
//
//
import axios, { AxiosError, HeadersDefaults, AxiosInstance, AxiosProgressEvent } from "axios";

import Validator            from "@common/utils/Validator";
import StringUtils          from '@common/utils/StringUtils';
import ObjectUtils          from "@common/utils/ObjectUtils";

import SysConstants         from "@common/utils/SysConstants";
import BrowserUtils         from "@common/utils/BrowserUtils";

import { RestfulEndpoint }  from './RestfulEndpoint';
import Network              from "./Network";


//
//
//
interface EndpointCache
{
    endpoint    : RestfulEndpoint;
    last_access : number;               // ms from 1970
    lifespan    : number;               // minutes
    reply       : RestfulService.Reply;
}


//
//
//
export class RestfulService
{
    private base_url            : string;
    private default_headers     : any;
    private default_timeout     : number;

    private client              : AxiosInstance;

    private cache               : Array<EndpointCache>;

    private timer               : ReturnType<typeof setInterval> | null;
    private cookies             : any = {};

    private csrfTag              : string | null = null;
    private csrfTzTag            : string | null = null;

    private callback_monitor  : RestfulService.CallHandle | null;

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    constructor( base_url       : string,
                default_headers : any = {},
                timeout         : number = 10 * SysConstants.Time.SECONDS_TO_MS,   // 10 seconds
                csrf_tag        : string | null = null,
                csrf_tz_tag     : string | null = null,
                monitor         : RestfulService.CallHandle | null = null )
    {
        this.base_url = base_url;

        this.callback_monitor = monitor;

        // csrf
        if( csrf_tag    != null && csrf_tag    != "" )this.csrfTag = csrf_tag.toLowerCase();      // future case insensitivity
        if( csrf_tz_tag != null && csrf_tz_tag != "" )this.csrfTzTag = csrf_tz_tag.toLowerCase();

        this.default_headers = { ...default_headers, ContentType: Network.MimeType.JSON };
        //this.default_headers = default_headers;
        this.default_headers[ 'ContentType' ]= Network.MimeType.JSON;
        //this.default_headers = default_headers;
        this.default_timeout = timeout;
        this.cache = [];
        this.client = axios.create( { baseURL: this.base_url, headers: this.default_headers } );
        this.client.defaults.withCredentials = true;
        this.timer = null;
        this.checkCache = this.checkCache.bind( this );
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public passHeaders( headers: any, names : Array<string> ) : void
    {
        let i : number;
        for( i=0; i < names.length; i++ )
        {
            if( headers[ names[i] ] )
            {
                this.setHeader( names[i], headers[ names[i] ] );
            }
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public setHeader( name: string, value : string | number | boolean ) : void
    {
        this.default_headers[name] = value;
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public setCookie( name: string, value : string | number | boolean ) : void
    {
        this.cookies[name] = value;
        let cookie_str : Array<string> = [];
        let cookie_keys : Array<string> = Object.keys( this.cookies );
        cookie_keys.forEach( ( cname : string ) => { cookie_str.push( cname + '=' + this.cookies[cname] ) } );

        this.default_headers['Cookie'] = cookie_str.join(';') ;
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public deleteHeader( name: string ) : void
    {
        if( Validator.notNull( this.default_headers[name] ) )
        {
            delete this.default_headers[name];
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public static error( response  : RestfulService.Reply, default_err? : string ) : string
    {
        return ( response.error && response.error?.message ) ? response.error.message : default_err ? default_err : "Unknown";
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public clear() : void
    {
        this.cache = [];
        if( this.timer !== null )
        {
            clearInterval( this.timer );
            this.timer = null;
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    public getHeaders() : HeadersDefaults
    {
        return this.default_headers;
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    private compareEndpts( endpt1 : RestfulEndpoint, endpt2 : RestfulEndpoint ) : boolean
    {
        // same urll and method
        if( endpt1.url == endpt2.url && endpt1.method == endpt2.method )
        {
            // same class, so campare requests
            //console.log("compare Endpts", JSON.stringify( endpt1.request ), JSON.stringify( endpt2.request ) );
            if( JSON.stringify( endpt1.request ) === JSON.stringify( endpt2.request ) )return true;
        }
        return false;
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    private checkCache() : void
    {
        //console.log("checkCache", this.cache.length );
        
        const now : number = new Date().getTime();
        let i : number = 0;
        while( i < this.cache.length )
        {
            if( this.cache[i].last_access + ( this.cache[i].lifespan * SysConstants.Time.MINUTES_TO_MS ) < now )
            {
                this.cache.splice( i, 1 );
            }
            else
            {
                i++;
            }
        }

        // cache is empty, so stop checking it
        if( this.cache.length === 0 && this.timer !== null )
        {
            //console.log("checkCache stopped" );
            clearInterval( this.timer );
            this.timer = null;
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////
    private findCache( endpt : RestfulEndpoint ) : EndpointCache | null
    {
        let i : number;
        for( i=0; i < this.cache.length; i++ )
        {
            if( this.compareEndpts( this.cache[i].endpoint, endpt ) )
            {
                // check if lifespan of cache has passed
                const now : number = new Date().getTime();
                if( this.cache[i].last_access + ( this.cache[i].lifespan * 1000*60 ) < now )
                {
                    console.log("cache died");
                    this.cache.splice( i, 1 );

                    // cache now empty, so stop checking it
                    if( this.cache.length === 0 && this.timer !== null )
                    {
                        clearInterval( this.timer );
                        this.timer = null;
                    }
                    return null;
                }
                else
                {
                    return this.cache[i];
                }
            }
        }
        return null;
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async head( url : string, parameters : any = null, data : any = {}, headers : any = {}, timeout: number | null = null ) : Promise<RestfulService.Reply>
    {
        return await this.request( Network.Method.HEAD, url, parameters, data, headers, timeout );
    }
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async post( url : string, parameters : any = null, data : any = {}, headers : any = {}, timeout: number | null = null ) : Promise<RestfulService.Reply>
    {
        return await this.request( Network.Method.POST, url, parameters, data, headers, timeout );
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async form(  url         : string,
                        parameters  : any = null,
                        data        : any = {},
                        headers     : any = {},
                        timeout     : number | null = null,
                        onProgress? : RestfulService.ProgressCallback ) : Promise<RestfulService.Reply>
    {
        const formData : FormData = new FormData();

        const keys : Array<string> = Object.keys( data );
        keys.forEach( ( key : string ) =>
        {
            if( data[key] !== undefined )
            {
                formData.append( key, data[key] );
            }
        } );

        return await this.request( Network.Method.POST, url, parameters, formData, headers, timeout, onProgress );
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async get(   url         : string,
                        parameters  : any = null,
                        headers     : any = {},
                        timeout     : number | null = null,
                        options     : RestfulService.Options | null = null ) : Promise<RestfulService.Reply>
    {
        let get_url : string = url;
        let args : string = RestfulService.queryString( parameters );
        if( args.length > 0 )get_url += ( '?' + args );
        let endpt : RestfulEndpoint = new RestfulEndpoint( Network.Method.GET, get_url, timeout );

        if( options != null && options.cache )
        {
            let cached : EndpointCache | null = this.findCache( endpt );
            if( cached != null )
            {
                cached.last_access = new Date().getTime();  // just update last accessed
                console.log( 'returned cached results', endpt.method, endpt.url );
                return cached.reply;
            }
        }

        let reply : RestfulService.Reply = await this.request( Network.Method.GET, url, parameters, null, headers, timeout );

        // check and store response if good
        if( options
            && Validator.notNull( options.cache )
            && options.cache === true
            && reply.ok )
        {
            //console.log( 'cache endpt results', endpt.url, endpt.method, reply.data );
            // create enpt container
            // todo: add parameters
            // ignores headers but that should be consistent for caching
            this.cache.push( {  endpoint: endpt,
                                last_access: new Date().getTime(),
                                reply: reply,
                                lifespan : options.lifespan ?? 15 } );

            // first one added, start timer
            if( this.cache.length == 1 )
            {
                this.timer = setInterval( this.checkCache, 1000*60*5 );   // 5 minute timer
            }
        }
        return reply;
    }
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async put( url : string, parameters : any = null, data : any = {}, headers : any = {}, timeout: number | null = null ) : Promise<RestfulService.Reply>
    {
        return await this.request( Network.Method.PUT, url, parameters, data, headers, timeout );
    }
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public async delete( url : string, parameters : any = null, data : any = {}, headers : any = {}, timeout: number | null = null ) : Promise<RestfulService.Reply>
    {
        return await this.request( Network.Method.DELETE, url, parameters, data, headers, timeout );
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    public static queryString( parameters : any ) : string
    {
        if( Validator.isNull( parameters ) )return "";

        let params : Array<string> = [];
        //let name : string;

        const keys : Array<string> = Object.keys( parameters );
        keys.forEach( ( key : string, index : number ) => {
            if( parameters[key] != undefined )
            {
                params.push( StringUtils.format( "{0}={1}", key, parameters[key].toString() ) );
            }
            
        } );

        return params.join('&');
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    private async request(  method      : Network.Method,
                            url         : string,
                            parameters  : any | null = null,
                            data        : any | null = null,
                            headers     : any | null = null,
                            timeout     : number | null = null,
                            onProgress? : RestfulService.ProgressCallback ) : Promise<RestfulService.Reply>
    {
        let reply : RestfulService.Reply = { ok: true, duration: 0, status: Network.Status.OK };
        let start : number = Date.now();

        try
        {
            let full_url : string = url;
            let args : string = RestfulService.queryString( parameters );
            //console.log('args', args, url );
            if( args.length > 0 )full_url += ( '?' + args );

            //console.log(">>>>>>>> REQUEST", method, url, ObjectUtils.merge( this.default_headers, headers ) );
            const response : any = await this.client.request( { 
                                                                url     : full_url,
                                                                method  : method.toLowerCase(),
                                                                data    : data,
                                                                headers : ObjectUtils.merge( this.default_headers, headers ),
                                                                timeout : timeout ?? this.default_timeout,
                                                                onUploadProgress: onProgress 
                                                            } );
            //console.log( 'resp', response );

            //console.log('<<<<<<<<<< RESPONSE headers', response.headers );

            //
            // get this last csrf header if provided
            //
            if( this.csrfTag != null )
            {
                const header_keys = Object.keys( response.headers );
                // case insensitive
                let i : number;
                for( i=0; i < header_keys.length; i++ )
                {
                    if( header_keys[i].toLowerCase() == this.csrfTag )
                    {
                        this.setHeader( this.csrfTag, response.headers[ header_keys[i] ] );

                        if( this.csrfTzTag != null )
                        {
                            this.setHeader( this.csrfTzTag, -1 * BrowserUtils.tzOffset() );    // why not negative for US???
                        }
                        
                        break;
                    }
                }

                //console.log('response CSRF', this.default_headers );
            }
            

            //
            // setup reply
            //
            reply.ok       = response.status === 200;
            reply.status   = response.status;
            reply.headers  = { ...response.headers };
            reply.data     = response.data;
            reply.duration = Date.now() - start;

            // callback monitor
            if( this.callback_monitor !== null )this.callback_monitor( method, url, response.status,reply.duration );
            return reply;
        }
        catch ( err : any )
        {
            //console.error( 'exception', err );

            const error        : AxiosError = err;

            // default
            let error_code     : string = 'EXCEPTION';
            let error_messsage : string = 'server error';
            let error_type     : string = '';
            let error_status   : number = Network.Status.INTERNAL_SERVER_ERROR;
            let error_data     : any | undefined = undefined;

            //
            reply.duration = Date.now() - start;
            reply.ok = false;

            // type errors like 4xx/5xx type error
            if( error.response )
            {
                //console.log('client data',error.response.data);
                //console.log('client status',error.response.status);
                //console.log('client headers',error.response.headers);
                error_status = error.response.status;

                // ideally, a non-200 response is thrown here and the body returns:
                // {  code   : machine readable code that client and interpret on more why it failed,
                //    message: a human readable message on more details of the code }
                if( error.response.data !== undefined && error.response.data !== null )
                {
                    if( ( error.response.data as any ).code    )error_code     = ( error.response.data as any ).code;
                    if( ( error.response.data as any ).message )error_messsage = ( error.response.data as any ).message;
                    if( ( error.response.data as any ).type    )error_type     = ( error.response.data as any ).type;

                    // add in any raw data that was returned in the error.  This is very atypical to do this
                    error_data = error.response.data;

                    // remove fields we already have
                    if( error_data['status'] )delete error_data['status'];
                    if( error_data['code'] )delete error_data['code'];
                    if( error_data['message'] )delete error_data['message'];
                    if( error_data['type'] )delete error_data['type'];
                }
                
            }
            // do mostly from a spotty network, backend not responding instantly, unauthorized or cors issue
            else if( error.request )
            {

            }
            else
            {
                error_code = 'UNKNOWN';
            }
            
            //
            reply.status   = error_status;
            reply.error = { status : error_status, code: error_code, message: error_messsage, type: error_type, data : error_data };

            // callback monitor
            if( this.callback_monitor !== null )this.callback_monitor( method, url, error_status, Date.now() - start );

            return reply;
        }
    }
}

export namespace RestfulService
{
    export interface Error
    {
        status      : number;
        code        : string;
        message?    : string;
        type?       : string;   // T for totp, L for email verification
        data?       : any;      // optioan raw data of the error
    }

    export interface Reply
    {
        ok       : boolean;
        data?    : any;
        status   : Network.Status;
        headers? : any;
        error?   : Error;
        duration : number;
    }

    export interface Options
    {
        timeout?  : number;
        cache?    : boolean;
        lifespan? : number; // lifespan of cache in minutes
        //ignoreUriService? : boolean;
        // autorefresh
    }

    export type CallHandle = ( method: Network.Method, url : string, status: number, duration: number ) => void;

    export type ProgressEvent = AxiosProgressEvent;
    export type ProgressCallback = ( progressEvent: ProgressEvent ) => void;

    //export type JsonPrimitive = string | number | boolean | null;
    //export type JsonValue  = JsonPrimitive | JsonObject | JsonArray;
    //export type JsonObject = { [key: string]: JsonValue };
    //export type JsonArray  = Array<JsonValue>;
}

export default RestfulService;

//
// eof
//